import { useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Table,
  NumberInput,
  SimpleGrid,
  Card,
  Divider,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { rateCardApi } from '../../../../services/api/rateCard';
import type { RateCard, RateCardRates, AudienceType, OneOnOneSdaType } from '../../../../types';
import { AUDIENCE_TYPE_LABELS, ONE_ON_ONE_SDA_LABELS, ONE_ON_ONE_SDA_TYPES } from '../../../../types';

interface EditRatesModalProps {
  opened: boolean;
  onClose: () => void;
  rateCard: RateCard;
  onSaved: () => void;
}

const AUDIENCE_TYPES: AudienceType[] = ['emerging', 'established', 'executive'];

const FLAT_RATE_CONFIG: { key: string; label: string }[] = [
  { key: 'huddle', label: 'Huddle' },
  { key: 'immersion', label: 'Immersion' },
  // MED-910 / E13-8: independently-editable Orientation + Wrap-Up rates.
  { key: 'orientation', label: 'Orientation (/hr)' },
  { key: 'wrap_up', label: 'Wrap-Up (/hr)' },
  { key: 'coach_debrief', label: 'Coach Debrief' },
  { key: 'workshop', label: 'Workshop' },
  { key: 'in_person_half_day', label: 'In-Person Half Day' },
  { key: 'in_person_full_day', label: 'In-Person Full Day' },
];

export function EditRatesModal({ opened, onClose, rateCard, onSaved }: EditRatesModalProps) {
  const [rates, setRates] = useState<RateCardRates>(
    () => JSON.parse(JSON.stringify(rateCard.rates)) as RateCardRates,
  );
  const [saving, setSaving] = useState(false);

  const updateGroupSessionRate = (
    tier: AudienceType,
    duration: string,
    value: number,
  ) => {
    setRates((prev) => ({
      ...prev,
      group_session: {
        ...prev.group_session,
        [tier]: {
          ...prev.group_session[tier],
          [duration]: value,
        },
      },
    }));
  };

  // MED-693: independently update a specific one_on_one_sda session-type table.
  const updateOneOnOneSdaRate = (
    sessionType: OneOnOneSdaType,
    tier: AudienceType,
    duration: string,
    value: number,
  ) => {
    setRates((prev) => ({
      ...prev,
      one_on_one_sda: {
        ...prev.one_on_one_sda,
        [sessionType]: {
          ...prev.one_on_one_sda?.[sessionType],
          [tier]: {
            ...prev.one_on_one_sda?.[sessionType]?.[tier],
            [duration]: value,
          },
        },
      },
    }));
  };

  const updateFlatRate = (key: string, value: number) => {
    setRates((prev) => ({
      ...prev,
      flat: { ...prev.flat, [key]: value },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await rateCardApi.update(rates);
      onSaved();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to update rates', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setRates(JSON.parse(JSON.stringify(rateCard.rates)) as RateCardRates);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          Edit Rates
        </Text>
      }
      size="xl"
    >
      <Stack gap="lg">
        {/* Group Session SDA */}
        <GroupSessionEditor
          durations={['60', '75', '90']}
          rates={rates}
          onChange={updateGroupSessionRate}
        />

        <Divider />

        {/* MED-693: 1:1 SDA — five session-type tables, each independently editable */}
        {ONE_ON_ONE_SDA_TYPES.map((sessionType) => (
          <Stack gap="md" key={sessionType}>
            <OneOnOneSdaEditor
              sessionType={sessionType}
              title={ONE_ON_ONE_SDA_LABELS[sessionType]}
              durations={['45', '60']}
              rates={rates}
              onChange={updateOneOnOneSdaRate}
            />
            <Divider />
          </Stack>
        ))}

        {/* Flat Rates */}
        <Stack gap="xs">
          <Text size="xs" fw={600} tt="uppercase" c="dimmed">
            Per-Event & Flat Rates
          </Text>
          <SimpleGrid cols={2} spacing="sm">
            {FLAT_RATE_CONFIG.map(({ key, label }) => (
              <Card key={key} withBorder p="xs" radius="sm">
                <Text size="xs" c="dimmed" mb={4}>
                  {label}
                </Text>
                <NumberInput
                  prefix="$"
                  value={(rates.flat as Record<string, number>)[key] / 100}
                  onChange={(val) => updateFlatRate(key, (Number(val) || 0) * 100)}
                  min={0}
                  decimalScale={0}
                  thousandSeparator=","
                  size="sm"
                />
              </Card>
            ))}
          </SimpleGrid>
        </Stack>

        {/* Footer */}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleReset}>
            Reset
          </Button>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Group Session tiered rate editor */
function GroupSessionEditor({
  durations,
  rates,
  onChange,
}: {
  durations: string[];
  rates: RateCardRates;
  onChange: (tier: AudienceType, duration: string, value: number) => void;
}) {
  return (
    <TieredEditorShell
      title="Group Session SDA"
      durations={durations}
      getValue={(tier, d) => rates.group_session[tier]?.[d] ?? 0}
      onChange={onChange}
    />
  );
}

/** MED-693: One-on-one SDA editor keyed by session type */
function OneOnOneSdaEditor({
  sessionType,
  title,
  durations,
  rates,
  onChange,
}: {
  sessionType: OneOnOneSdaType;
  title: string;
  durations: string[];
  rates: RateCardRates;
  onChange: (
    sessionType: OneOnOneSdaType,
    tier: AudienceType,
    duration: string,
    value: number,
  ) => void;
}) {
  return (
    <TieredEditorShell
      title={title}
      durations={durations}
      getValue={(tier, d) => rates.one_on_one_sda?.[sessionType]?.[tier]?.[d] ?? 0}
      onChange={(tier, d, value) => onChange(sessionType, tier, d, value)}
    />
  );
}

/** Shared tiered editor table — audience × duration grid */
function TieredEditorShell({
  title,
  durations,
  getValue,
  onChange,
}: {
  title: string;
  durations: string[];
  getValue: (tier: AudienceType, duration: string) => number;
  onChange: (tier: AudienceType, duration: string, value: number) => void;
}) {
  return (
    <Stack gap="xs">
      <Text size="xs" fw={600} tt="uppercase" c="dimmed">
        {title}
      </Text>
      <Table verticalSpacing="xs" horizontalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Audience Type</Table.Th>
            {durations.map((d) => (
              <Table.Th key={d} style={{ textAlign: 'right' }}>
                {d} min
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {AUDIENCE_TYPES.map((tier) => (
            <Table.Tr key={tier}>
              <Table.Td>
                <Text size="sm" fw={500}>
                  {AUDIENCE_TYPE_LABELS[tier]}
                </Text>
              </Table.Td>
              {durations.map((d) => (
                <Table.Td key={d}>
                  <NumberInput
                    prefix="$"
                    value={getValue(tier, d) / 100}
                    onChange={(val) => onChange(tier, d, (Number(val) || 0) * 100)}
                    min={0}
                    decimalScale={0}
                    thousandSeparator=","
                    size="xs"
                    styles={{ input: { textAlign: 'right' } }}
                  />
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
